import {
  Box,
  DebtRepaymentProjection,
  DebtRepaymentSuggestion,
  Disclaimer,
  Div,
  P,
  Separator,
  displayCentsInDollars,
  lightTheme,
} from '@vgrubs/components';
import { formatDay } from '../helpers/receivablesLabels';
import {
  durationLabel,
  monthsLabel,
  perMonth,
  projectionMethods,
  targetLabel,
  targetMonthName,
} from '../helpers/repaymentSuggestionLabels';
import { StatusBadge } from './ReceivablesCells';
import { Duration, FigureRow } from './RepaymentSuggestion.styled';

type Props = {
  data: DebtRepaymentSuggestion;
  /** The option on show: the answer's own suggestion (A) or option B's. */
  projection: DebtRepaymentProjection;
};

/**
 * One of the AI's plans as numbers: how long it takes and when it is done, the three
 * methods with what each brings in a month, and the month's total against
 * what the target needs. Every figure is the backend's; nothing is worked
 * out here.
 */
export const SuggestedPlan = ({ data, projection: suggestion }: Props) => {
  const { targetMonths, requiredMonthly } = data;
  const duration = durationLabel(suggestion);
  // Per order is withheld from the payouts that would otherwise land on the
  // blue card, so with both on the card only adds what is left over.
  const sharesPayouts =
    suggestion.fromLocationBalance && suggestion.perOrderAmount != null;

  return (
    <Box $flex $column $gap="12px">
      <Div $flex $between $alignStart $gap="12px" $columnSM>
        {duration ? (
          <div>
            <Disclaimer $m="0" $color={lightTheme.lightGray}>
              Repaid in about
            </Disclaimer>
            <Duration>{duration}</Duration>
            {suggestion.estimatedCompletionDate && (
              <P $m="0">
                Done by {formatDay(suggestion.estimatedCompletionDate)}
              </P>
            )}
          </div>
        ) : (
          <P $m="0" $bold>
            This plan would not collect anything.
          </P>
        )}
        {targetMonths != null && suggestion.meetsTarget != null && (
          <StatusBadge
            color={
              suggestion.meetsTarget ? lightTheme.green : lightTheme.yellow
            }
          >
            {suggestion.meetsTarget ? 'Meets' : 'Misses'}{' '}
            {targetLabel(targetMonths)}
          </StatusBadge>
        )}
      </Div>

      <Separator />

      <div>
        {/* The headings carry the units, so a cell is only its figure: with
            "/ month" in every cell they wrapped on a phone. */}
        <FigureRow $columns={2}>
          <span />
          <Disclaimer $m="0" $color={lightTheme.lightGray}>
            Amount
          </Disclaimer>
          <Disclaimer $m="0" $color={lightTheme.lightGray}>
            Per month
          </Disclaimer>
        </FigureRow>
        {projectionMethods(suggestion).map((method) => (
          <FigureRow key={method.label} $columns={2}>
            <P $m="0">{method.label}</P>
            <P
              $m="0"
              $bold={method.value !== 'Off'}
              $color={method.value === 'Off' ? lightTheme.lightGray : undefined}
            >
              {method.value}
            </P>
            <P $m="0" $color={lightTheme.lightGray}>
              {method.monthly != null
                ? displayCentsInDollars(method.monthly)
                : '-'}
            </P>
          </FigureRow>
        ))}
        <FigureRow $columns={2} $strong>
          <P $m="0" $bold>
            Total
          </P>
          <span />
          <P $m="0" $bold>
            {displayCentsInDollars(suggestion.monthlyTotal)}
          </P>
        </FigureRow>
      </div>

      {(requiredMonthly != null || sharesPayouts) && (
        <Div $flex $column $gap="4px">
          {requiredMonthly != null && targetMonths != null && (
            <Disclaimer $m="0" $color={lightTheme.lightGray}>
              Repaying {displayCentsInDollars(data.remainingDebt)} by{' '}
              {targetMonthName(targetMonths)} ({monthsLabel(targetMonths)})
              needs {perMonth(requiredMonthly)}.
            </Disclaimer>
          )}
          {sharesPayouts && (
            <Disclaimer $m="0" $color={lightTheme.lightGray}>
              Per order comes out of the same payouts that land on the vPay
              Card, so the vPay Card only adds what per order leaves.
            </Disclaimer>
          )}
        </Div>
      )}
    </Box>
  );
};
